import { Airport } from '@ibe.models/airport.model';
import { PassengerFlightInfo, AncillariesLimitations } from '@ibe.models/passenger-flight-info.model';
import { Passenger } from '@ibe.models/passenger.model.lib';
import { getBrandFeatures } from '@ibe/models/ui';
import { Airplane } from './airplane.model';


export class Flight {
	id: string;
	number: string;
	direct: boolean;
	airline: [{
		logo: string;
		name: string;
		short: string;
		airplane: Airplane;
		starAlliance: boolean;
	}];
	departure: {
		airport: Airport;
		time: Date;
	};
	arrival: {
		airport: Airport;
		time: Date;
	};
	options: {
        idOffer: any;
		price: number;
		brand: FlightBrand;
		features: Array<Object>;
	};

	status: FlightStatus = 'unknown';
	update: 'new-flight' | 'schedule-update';
	check_in: {
		status: 'not available' | 'open' | 'done';
		time: Date;
	};
	passengerInfo: PassengerFlightInfo[];
	stops?: {
		airport: Airport;
		time: Date;
		departure: Date;
		ancillaries_limitations?: AncillariesLimitations;
	}[];

	stops_labels: {
		id: string;
		label: string;
	}[];

	economy?: PriceDescriptor;
	business?: PriceDescriptor;
	is_stopover?: boolean;
	isOpen?: boolean;

	stopover: FlightStopover;

	ancillaries_limitations?: AncillariesLimitations;
	listSegment?: any;
	brandList?: any;
    numberOfStops?: any;
    bound: string;

	constructor(departure, arrival, number: string) {
		this.departure = departure;
		this.arrival = arrival;
		this.number = number;
		this.passengerInfo = [];
	}

	canEditPassengerInfo(status: FlightStatus = this.status): boolean {
		return ['scheduled', 'active', 'unknown'].includes(status);
	}

	setStopover(stopover: FlightStopover) {
		this.stopover = stopover;
		this.is_stopover = true;
	}

	/**
	 * Creates passenger info for the given passenger in this flight,
	 * including luggage information considering the flight brand
	 * @param p {Passenger} the passenger to assign to the flight
	 * @return The generated PassengerFlightInfo object added to the flight
	 */
	assignPassenger(p: Passenger) {
		/**
		 * Generate stop labels in flight and passenger info
		 */
		let stops = [{
			id: `${this.departure.airport.code.toLowerCase()}-${this.arrival.airport.code.toLowerCase()}`,
			label: `${this.departure.airport.code}-${this.arrival.airport.code}`,
			number_flight: this.number[0],
			departure: this.departure,
			arrival: this.arrival,
			ancillaries_limitations: this.ancillaries_limitations
		}];

		if (this.stops && this.stops.length) {
			stops = this.stops.reduce((acc, cur, idx, src) => {

				if (acc.length == 0) {
					acc.push({
						id: `${this.departure.airport.code.toLowerCase()}-${src[idx].airport.code.toLowerCase()}`,
						label: `${this.departure.airport.code}-${src[idx].airport.code}`,
						number_flight: this.number[idx],
						departure: this.departure,
						arrival: src[idx],
						ancillaries_limitations: this.ancillaries_limitations
					});
				} else {
					acc.push({
						id: `${src[idx - 1].airport.code.toLowerCase()}-${cur.airport.code.toLowerCase()}`,
						label: `${src[idx - 1].airport.code}-${cur.airport.code}`,
						number_flight: this.number[idx],
						departure: src[idx - 1],
						arrival: cur,
						ancillaries_limitations: cur.ancillaries_limitations
					});
				}

				if (acc.length === src.length) {
					acc.push({
						id: `${src[idx].airport.code.toLowerCase()}-${this.arrival.airport.code.toLowerCase()}`,
						label: `${src[idx].airport.code}-${this.arrival.airport.code}`,
						number_flight: this.number[idx],
						departure: src[idx],
						arrival: this.arrival,
						ancillaries_limitations: src[idx].ancillaries_limitations
					});
				}

				return acc;

			}, []);
		}

		this.stops_labels = stops;

		const pInfo = new PassengerFlightInfo(p, this.options.brand);

		pInfo.createStopsAnciallaries({ stops });

		this.passengerInfo.push(pInfo);

		return pInfo;
	}

	resetExtras() {
		this.passengerInfo = this.passengerInfo.map(pass => {

			const newInfo = {
				boughtSeat: null,
				added_bags: [],
				loungeEnabled: false,
				fastTrackEnabled: false,
				specialMeals: null,
				sportsEquipment: [],
				pets: [],
				musicInstruments: []
			};
			const stops = pass.stops.map(info => Object.assign({}, info, newInfo));

			return Object.assign(new PassengerFlightInfo(null, null), pass, { stops });
		});
	}

}

interface PriceDescriptor { lowest_price: number; }
export type FlightBrand = 'discount' | 'basic' | 'classic' | 'plus' | 'executive' | 'top-executive' | 'miles&go-economy' | 'miles&go-extra' | 'miles&go-executive' | 'star_alliance-economy' | 'star_alliance-business' | 'star_alliance-first';
export type FlightStatus = 'scheduled' | 'active' | 'unknown' | 'redirected' | 'landed' | 'diverted' | 'cancelled' | 'not operational' | 'flown' | 'new';

export type FlightStopover = {
	airport: Airport,
	days: number,
	when?: any
};

export type FlightFilter = {
	directFlights: boolean;
	cabinOptions: string;
	schedule: Array<{ departure: any, arrival: any }>;
	airline: boolean;
	airlines: Array<string>;
	step: number;
	baggageIncluded: boolean;
	onlyRefundable: boolean;
	bookAwardFlights: boolean;
	starAlliance: boolean;
};


export class FeaturesMap {
	flight: Flight;

	propertiesMapping = {
		seats: {
			changed: this.seatsChanged,
			count: this.seatCount,
			cost: this.seatCosts,
			count_added: this.seatCountAdded,
			enable: this.seatEnable
		},
		bags: {
			changed: this.bagsChanged,
			count: this.bagsCount,
			cost: this.bagsCost,
			count_added: this.bagsCountAdded,
			enable: this.bagsEnable
		},
		fasttrack: {
			changed: this.fastTrackChanged,
			count: this.fastTrackCount,
			cost: this.fastTrackCost,
			count_added: this.fastTrackCountAdded,
			enable: this.fastTrackEnable
		},
		lounge: {
			changed: this.loungeChanged,
			count: this.loungeCount,
			cost: this.loungeCost,
			count_added: this.loungeCountAdded,
			enable: this.loungeEnable
		},
		sports: {
			changed: this.sportsChanged,
			count: this.sportsCount,
			cost: this.sportsCost,
			count_added: this.sportsCountAdded,
			enable: this.sportsEnable
		},
		instruments: {
			changed: this.instrumentsChanged,
			count: this.instrumentsCount,
			cost: this.instrumentsCost,
			count_added: this.instrumentsCountAdded,
			enable: this.instrumentsEnable
		},
		pets: {
			changed: this.petsChanged,
			count: this.petsCount,
			cost: this.petsCost,
			count_added: this.petsCountAdded,
			enable: this.petsEnable
		},
		meals: {
			changed: this.mealsChanged,
			count: this.mealsCount,
			cost: this.mealsCost,
			count_added: this.mealsCountAdded,
			enable: this.mealsEnable
		},
		'preferred-boarding': {
			changed: this.preferredBChanged,
			count: this.preferredBCount,
			cost: this.preferredBCost,
			count_added: this.preferredBCountAdded,
			enable: this.preferredBoardingEnabled
		}
	};

	constructor(flight: Flight) {
		this.flight = flight;
	}

	getCost(feature) {
		const property = this.propertiesMapping[feature.id];
		return this.flight && feature.id && property ? property.cost.apply(this) : 0;
	}

	hasChanged(feature) {
		const property = this.propertiesMapping[feature.id];
		return this.flight && feature.id && property ? property.changed.apply(this) : false;
	}

	getCount(feature) {
		const property = this.propertiesMapping[feature.id];
		return this.flight && feature.id && property ? property.count.apply(this) : 0;
	}

	getCountAdded(feature) {
		const property = this.propertiesMapping[feature.id];
		return this.flight && feature.id && property ? property.count_added.apply(this) : 0;
	}

	getEnable(feature) {
		const property = this.propertiesMapping[feature.id];
		return this.flight && feature.id && property ? property.enable.apply(this) : true;
	}


	private limitationByFlight(item): boolean {
		let limitation = false;

		if (typeof this.flight.ancillaries_limitations != 'undefined') {
			if (typeof this.flight.ancillaries_limitations[item] != 'undefined') {
				limitation = this.flight.ancillaries_limitations[item];
			}
		}

		return limitation;
	}

	private limitationByPassenger(item): boolean {
		return this.flight.passengerInfo.filter(cur => cur.passenger.ancillaries_limitations && cur.passenger.ancillaries_limitations[item]).length > 0;
	}

	private enable(item) {
		const limitationByFlight = this.limitationByFlight(item);
		const limitationByPassenger = this.limitationByPassenger(item);

		if (limitationByFlight) return false;
		if (limitationByPassenger) return false;

		return true;
	}

	private seatsChanged() {
		return this.flight.passengerInfo.some(passenger => passenger.stops.some(stop => !!stop.boughtSeat));
	}

	private seatCount() {
		return this.flight.passengerInfo.reduce(((sum, passenger) => sum += passenger.stops.reduce((acc, stop) => acc += (stop.boughtSeat ? 1 : 0), 0)), 0);
	}

	private seatCountAdded() {
		return this.flight.passengerInfo.reduce(((sum, passenger) => sum += passenger.stops.reduce((acc, stop) => acc += (stop.added && stop.added.boughtSeat ? 1 : 0), 0)), 0);
	}

	private seatCosts() {
		return this.flight.passengerInfo.reduce(((sum, passenger) => sum += passenger.stops.filter(cur => cur.boughtSeat).reduce((acc, stop) => acc += stop.boughtSeat.price, 0)), 0);
	}

	private seatEnable() { return this.enable('seat'); }

	private bagsChanged() {
		return this.flight.passengerInfo.some(passenger => passenger.stops.some(stop => stop.added_bags && stop.added_bags.length > 0));
	}

	private bagsCount() {
		/**
		* Get the first stop and duplicate info for other flights
		*/
		return this.flight.passengerInfo.filter(cur => cur.stops[0].added_bags).reduce(((sum, passenger) => sum += passenger.stops[0].added_bags.length), 0);
	}

	private bagsCountAdded() {
		/**
		* Get the first stop and duplicate info for other flights
		*/
		return this.flight.passengerInfo.reduce(((sum, passenger) => sum += (passenger.stops[0].added && passenger.stops[0].added.added_bags ? passenger.stops[0].added.added_bags.length : 0)), 0);
	}

	private bagsCost() {
		const eachCost = (sum, item) => sum += parseFloat(item.added_bags.reduce((acc, bag) => acc += bag, 0));
		return this.flight.passengerInfo.reduce((sum, passenger) => sum += parseFloat(passenger.stops.filter(cur => cur.added_bags).reduce(eachCost, 0)), 0);
	}

	private bagsEnable() { return this.enable('added_bags'); }

	private fastTrackChanged() {
		return this.flight.passengerInfo.some(passenger => passenger.stops.some(stop => stop.fastTrackEnabled));
	}

	private fastTrackCount() {
		/**
		* Get the first stop and duplicate info for other flights
		*/
		return this.flight.passengerInfo.reduce((sum, passenger) => sum += (passenger.stops[0].fastTrackEnabled ? 1 : 0), 0);
	}

	private fastTrackCountAdded() {
		/**
		* Get the first stop and duplicate info for other flights
		*/
		return this.flight.passengerInfo.reduce((sum, passenger) => sum += (passenger.stops[0].added && passenger.stops[0].added.fastTrackEnabled && passenger.stops[0].added.fastTrackEnabled ? 1 : 0), 0);
	}

	private fastTrackEnable() { return this.enable('fastTrackEnabled'); }

	// TODO remove fixed cost
	private fastTrackCost() {
		const price = getBrandFeatures(this.flight.options.brand).filter(cur => cur.type == 'extra').find(cur => cur.id == 'fasttrack').price;
		return this.flight.passengerInfo.reduce((sum, passenger) => sum + passenger.stops.filter(cur => cur.fastTrackEnabled).length * price, 0);
	}

	private loungeChanged() {
		return this.flight.passengerInfo.some(passenger => passenger.stops.some(stop => stop.loungeEnabled));
	}

	private loungeCount() {
		/**
		* Get the first stop and duplicate info for other flights
		*/
		return this.flight.passengerInfo.reduce((sum, passenger) => sum += (passenger.stops[0].loungeEnabled ? 1 : 0), 0);
	}

	private loungeCountAdded() {
		/**
		* Get the first stop and duplicate info for other flights
		*/
		return this.flight.passengerInfo.reduce((sum, passenger) => sum += (passenger.stops[0].added && passenger.stops[0].added.loungeEnabled ? 1 : 0), 0);
	}

	private loungeCost() {
		const price = getBrandFeatures(this.flight.options.brand).filter(cur => cur.type == 'extra').find(cur => cur.id == 'lounge').price;
		return this.flight.passengerInfo.reduce((sum, passenger) => sum += passenger.stops.filter(cur => cur.loungeEnabled).length * price, 0);
	}

	private loungeEnable() { return this.enable('loungeEnabled'); }



	private sportsChanged() {
		return this.flight.passengerInfo.some(passenger => passenger.stops.some(stop => stop.sportsEquipment && stop.sportsEquipment.length > 0));
	}

	private sportsCount() {
		/**
		* Get the first stop and duplicate info for other flights
		*/
		return this.flight.passengerInfo.reduce((sum, passenger) => sum += (passenger.stops[0].sportsEquipment && passenger.stops[0].sportsEquipment.length ? passenger.stops[0].sportsEquipment.length : 0), 0);
	}

	private sportsCountAdded() {
		/**
		* Get the first stop and duplicate info for other flights
		*/
		return this.flight.passengerInfo.reduce((sum, passenger) => sum += (passenger.stops[0].added && passenger.stops[0].added.sportsEquipment && passenger.stops[0].added.sportsEquipment.length ? passenger.stops[0].added.sportsEquipment.length : 0), 0);
	}

	private sportsCost() {
		const eachCost = (sum, equipment) => sum += equipment.sportsEquipment.reduce((acc, cur) => acc += cur.price, 0);
		return this.flight.passengerInfo.reduce((sum, passenger) => sum += passenger.stops.filter(cur => cur.sportsEquipment).reduce(eachCost, 0), 0);
	}

	private sportsEnable() { return this.enable('sportsEquipment'); }


	private instrumentsChanged() {
		return this.flight.passengerInfo.some(passenger => passenger.stops.some(stop => stop.musicInstruments && stop.musicInstruments.length > 0));
	}

	private instrumentsCount() {
		/**
		* Get the first stop and duplicate info for other flights
		*/
		return this.flight.passengerInfo.reduce((sum, passenger) => sum += (passenger.stops[0].musicInstruments && passenger.stops[0].musicInstruments.length ? passenger.stops[0].musicInstruments.length : 0), 0);
	}

	private instrumentsCountAdded() {
		/**
		* Get the first stop and duplicate info for other flights
		*/
		return this.flight.passengerInfo.reduce((sum, passenger) => sum += (passenger.stops[0].added && passenger.stops[0].added.musicInstruments && passenger.stops[0].added.musicInstruments.length ? passenger.stops[0].added.musicInstruments.length : 0), 0);
	}

	private instrumentsCost() {
		const eachCost = (sum, item) => sum += item.musicInstruments.reduce((acc, cur) => acc += cur.price, 0);
		return this.flight.passengerInfo.reduce((sum, passenger) => sum += passenger.stops.filter(cur => cur.musicInstruments).reduce(eachCost, 0), 0);
	}

	private instrumentsEnable() { return this.enable('musicInstruments'); }


	private petsChanged() {
		return this.flight.passengerInfo.some(passenger => passenger.stops.some(stop => stop.pets && stop.pets.length > 0));
	}

	private petsCount() {
		/**
		* Get the first stop and duplicate info for other flights
		*/
		return this.flight.passengerInfo.reduce((sum, passenger) => sum += (passenger.stops[0].pets && passenger.stops[0].pets.length ? passenger.stops[0].pets.length : 0), 0);
	}

	private petsCountAdded() {
		/**
		* Get the first stop and duplicate info for other flights
		*/
		return this.flight.passengerInfo.reduce((sum, passenger) => sum += (passenger.stops[0].added && passenger.stops[0].added.pets && passenger.stops[0].added.pets.length ? passenger.stops[0].added.pets.length : 0), 0);
	}

	private petsCost() {
		const eachCost = (sum, item) => sum += item.pets.reduce((acc, cur) => acc += cur.price, 0);
		return this.flight.passengerInfo.reduce((sum, passenger) => sum += passenger.stops.filter(cur => cur.pets).reduce(eachCost, 0), 0);
	}


	private petsEnable() { return this.enable('pets'); }


	private mealsChanged() {
		return this.flight.passengerInfo.some(passenger => passenger.stops.some(stop => !!stop.specialMeals));
	}

	private mealsCount() {
		return this.flight.passengerInfo.reduce((sum, passenger) => sum += passenger.stops.filter(cur => cur.specialMeals).reduce((acc, stop) => acc += (stop.specialMeals ? 1 : 0), 0), 0);
	}

	private mealsCountAdded() {
		return this.flight.passengerInfo.reduce((sum, passenger) => sum += passenger.stops.filter(cur => cur.added.specialMeals).reduce((acc, stop) => acc += (stop.added.specialMeals ? 1 : 0), 0), 0);
	}

	private mealsCost() {
		const sumMeals = (sum, passenger) => (sum + ((passenger.specialMeals && typeof passenger.specialMeals.price != 'undefined' && passenger.specialMeals.price) ? passenger.specialMeals.price : 0));
		return this.flight.passengerInfo.reduce(sumMeals, 0);
	}

	private mealsEnable() { return this.enable('specialMeals'); }

	private preferredBChanged() {
		return this.flight.passengerInfo.some(passenger => passenger.stops.some(stop => stop.preferredBoardingEnabled));
	}

	private preferredBCount() {
		/**
		* Get the first stop and duplicate info for other flights
		*/
		return this.flight.passengerInfo.filter( passenger => passenger.stops[0].preferredBoardingEnabled).length;
	}

	private preferredBCountAdded() {
		/**
		* Get the first stop and duplicate info for other flights
		*/
		return this.flight.passengerInfo
			.filter( passenger => passenger.stops[0].added && passenger.stops[0].added.preferredBoardingEnabled)
			.length;
	}

	private preferredBoardingEnabled() {
		return true;
	}

	private preferredBCost() {
		const price = getBrandFeatures(this.flight.options.brand).
					filter(cur => cur.type == 'extra').
					find(cur => cur.id === 'preferred-boarding').price;
		//
		// Assuming that there is only one per flight
		const nPassengers = this.flight.passengerInfo.filter( passenger => passenger.stops[0].preferredBoardingEnabled).length;
		return nPassengers * price;
	}

}
